import React, { useState, useEffect, useCallback, useRef } from 'react';
import { HardDrive, Trash2, Loader2, AlertTriangle, RefreshCw, FolderOpen, User, Database, Cloud, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '../../config/supabase';
import { formatFileSize } from '../../utils/file-types';
import { cn } from '../../utils/cn';
import ConfirmModal from '../shared/ConfirmModal';
import { deleteFromR2 } from '../../config/storage';

interface FolderStats {
  user_folder: string;
  user_email: string | null;
  file_count: number;
  size_bytes: number;
}

interface OrphanFile {
  key: string;
  size: number;
}

interface StorageCleanupProps {
  isOpen: boolean;
  onClose: () => void;
}

const StorageCleanup: React.FC<StorageCleanupProps> = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [folderStats, setFolderStats] = useState<FolderStats[]>([]);
  const [totalDbSize, setTotalDbSize] = useState(0);
  const [totalDbFiles, setTotalDbFiles] = useState(0);
  const [r2TotalSize, setR2TotalSize] = useState(0);
  const [r2TotalFiles, setR2TotalFiles] = useState(0);
  const [orphanFiles, setOrphanFiles] = useState<OrphanFile[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState<{ current: number; total: number } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const cancelRef = useRef(false);

  // 使用分页获取所有数据库文件统计
  const fetchFolderStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const PAGE_SIZE = 1000;
      const statsMap = new Map<string, FolderStats>();
      let offset = 0;
      let hasMore = true;
      let totalSize = 0;
      let totalCount = 0;

      while (hasMore) {
        const { data, error: queryError } = await supabase
          .from('files')
          .select('storage_key, size, created_by')
          .is('deleted_at', null)
          .range(offset, offset + PAGE_SIZE - 1);
        
        if (queryError) throw queryError;
        
        if (!data || data.length === 0) {
          hasMore = false;
        } else {
          for (const file of data) {
            const folder = file.storage_key?.split('/')[0] || 'unknown';
            totalSize += file.size || 0;
            totalCount++;
            
            if (!statsMap.has(folder)) {
              statsMap.set(folder, {
                user_folder: folder,
                user_email: null,
                file_count: 0,
                size_bytes: 0
              });
            }
            
            const stats = statsMap.get(folder)!;
            stats.file_count++;
            stats.size_bytes += file.size || 0;
          }
          
          offset += data.length;
          if (data.length < PAGE_SIZE) hasMore = false;
        }
      }

      // 获取用户邮箱
      const folderIds = Array.from(statsMap.keys());
      if (folderIds.length > 0) {
        const { data: users } = await supabase
          .from('profiles')
          .select('id, email')
          .in('id', folderIds);
        
        if (users) {
          for (const user of users) {
            const stats = statsMap.get(user.id);
            if (stats) stats.user_email = user.email;
          }
        }
      }

      const statsArray = Array.from(statsMap.values()).sort((a, b) => b.size_bytes - a.size_bytes);
      setFolderStats(statsArray);
      setTotalDbSize(totalSize);
      setTotalDbFiles(totalCount);
    } catch (err: any) {
      console.error('获取文件夹统计失败:', err);
      setError(err.message || '获取统计信息失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchFolderStats();
    }
  }, [isOpen, fetchFolderStats]);

  // 扫描 R2 存储并对比数据库
  const scanOrphanFiles = async () => {
    setIsScanning(true);
    setScanProgress('正在获取数据库文件列表...');
    setOrphanFiles([]);
    setError(null);
    
    try {
      // 1. 获取数据库中所有 storage_key
      const PAGE_SIZE = 1000;
      const dbKeys = new Set<string>();
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        setScanProgress(`正在获取数据库记录... (${dbKeys.size} 条)`);
        const { data, error } = await supabase
          .from('files')
          .select('storage_key')
          .range(offset, offset + PAGE_SIZE - 1);
        
        if (error) throw error;
        
        if (!data || data.length === 0) {
          hasMore = false;
        } else {
          for (const file of data) {
            if (file.storage_key) dbKeys.add(file.storage_key);
          }
          offset += data.length;
          if (data.length < PAGE_SIZE) hasMore = false;
        }
      }

      setScanProgress(`数据库共有 ${dbKeys.size} 个文件记录。正在获取用户目录列表...`);

      // 获取当前系统的用户目录列表（从数据库中获取所有用户 ID）
      const { data: profiles } = await supabase.from('profiles').select('id');
      const validUserFolders = new Set<string>(profiles?.map(p => p.id) || []);
      
      // UUID 正则表达式，用于验证目录名是否为 UUID 格式
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

      setScanProgress(`数据库共有 ${dbKeys.size} 个文件记录，${validUserFolders.size} 个用户。正在获取 R2 存储文件列表...`);

      // 2. 调用 R2 Edge Function 获取存储中的文件列表
      let r2Keys: { key: string; size: number }[] = [];
      let continuationToken: string | undefined = undefined;
      let r2TotalSize = 0;
      let r2FileCount = 0;

      do {
        setScanProgress(`正在获取 R2 文件列表... (${r2FileCount} 个)`);
        
        const { data, error } = await supabase.functions.invoke('r2-storage', {
          body: {
            action: 'listObjects',
            prefix: '',
            maxKeys: 1000,
            continuationToken,
          },
        });

        if (error) throw new Error(`R2 列表失败: ${error.message}`);
        
        const objects = data?.objects || [];
        const isTruncated = data?.isTruncated;
        continuationToken = data?.continuationToken;

        for (const obj of objects) {
          r2Keys.push({ key: obj.key, size: obj.size || 0 });
          r2TotalSize += obj.size || 0;
          r2FileCount++;
        }

        if (!isTruncated) break;
      } while (continuationToken);

      setR2TotalSize(r2TotalSize);
      setR2TotalFiles(r2FileCount);

      setScanProgress(`R2 共有 ${r2FileCount} 个文件。正在对比...`);

      // 3. 找出孤儿文件（只检查当前系统的用户目录）
      const orphans: OrphanFile[] = [];
      let skippedOtherSystem = 0;
      
      for (const r2File of r2Keys) {
        // 获取文件所属的顶级目录
        const topFolder = r2File.key.split('/')[0];
        
        // 跳过非 UUID 格式的目录（这些是其他系统的，如 book-covers/, avatars/, thumbnails/, posters/ 等）
        if (!uuidRegex.test(topFolder)) {
          skippedOtherSystem++;
          continue;
        }
        
        // 跳过不属于当前系统用户的目录
        if (!validUserFolders.has(topFolder)) {
          skippedOtherSystem++;
          continue;
        }
        
        // 检查是否在数据库中存在
        if (!dbKeys.has(r2File.key)) {
          orphans.push({ key: r2File.key, size: r2File.size });
        }
      }

      setOrphanFiles(orphans);
      
      const orphanSize = orphans.reduce((sum, f) => sum + f.size, 0);
      setScanProgress(`✅ 扫描完成！\n\n📊 数据库: ${dbKeys.size} 个文件\n☁️ R2 存储: ${r2FileCount} 个文件\n⏭️ 跳过其他系统: ${skippedOtherSystem} 个\n🔴 孤儿文件: ${orphans.length} 个 (${formatFileSize(orphanSize)})`);
      
    } catch (err: any) {
      console.error('扫描失败:', err);
      setError(err.message || '扫描失败');
      setScanProgress('');
    } finally {
      setIsScanning(false);
    }
  };

  // 删除孤儿文件
  const deleteOrphanFiles = async () => {
    setShowDeleteConfirm(false);
    setIsDeleting(true);
    cancelRef.current = false;
    setDeleteProgress({ current: 0, total: orphanFiles.length });
    
    let deleted = 0;
    let failed = 0;
    const BATCH_SIZE = 50;

    try {
      for (let i = 0; i < orphanFiles.length; i += BATCH_SIZE) {
        if (cancelRef.current) break;
        
        const batch = orphanFiles.slice(i, i + BATCH_SIZE);
        const results = await Promise.allSettled(
          batch.map(file => deleteFromR2(file.key))
        );
        
        for (const result of results) {
          if (result.status === 'fulfilled') deleted++;
          else failed++;
        }
        
        setDeleteProgress({ current: i + batch.length, total: orphanFiles.length });
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      setScanProgress(`✅ 清理完成！成功删除 ${deleted} 个文件${failed > 0 ? `，失败 ${failed} 个` : ''}`);
      setOrphanFiles([]);
      
    } catch (err: any) {
      setError(err.message || '删除失败');
    } finally {
      setIsDeleting(false);
      setDeleteProgress(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-orange-500 to-red-500">
          <div className="flex items-center gap-3 text-white">
            <HardDrive size={24} />
            <div>
              <h2 className="text-lg font-semibold">存储清理面板</h2>
              <p className="text-sm text-white/80">扫描并清理孤儿文件</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
          >
            ✕
          </button>
        </div>

        {/* 内容 */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* 总览卡片 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-sky-50 to-sky-100 border border-sky-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-sky-500 text-white rounded-lg">
                  <Database size={20} />
                </div>
                <div>
                  <p className="text-xs text-sky-600">数据库文件</p>
                  <p className="text-xl font-bold text-sky-900">{totalDbFiles.toLocaleString()}</p>
                  <p className="text-xs text-sky-600">{formatFileSize(totalDbSize)}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500 text-white rounded-lg">
                  <Cloud size={20} />
                </div>
                <div>
                  <p className="text-xs text-purple-600">R2 存储</p>
                  <p className="text-xl font-bold text-purple-900">{r2TotalFiles > 0 ? r2TotalFiles.toLocaleString() : '未扫描'}</p>
                  <p className="text-xs text-purple-600">{r2TotalFiles > 0 ? formatFileSize(r2TotalSize) : '点击扫描'}</p>
                </div>
              </div>
            </div>
            
            <div className={cn(
              "border rounded-xl p-4",
              orphanFiles.length > 0 
                ? "bg-gradient-to-br from-red-50 to-red-100 border-red-200"
                : "bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200"
            )}>
              <div className="flex items-center gap-3">
                <div className={cn(
                  "p-2 text-white rounded-lg",
                  orphanFiles.length > 0 ? "bg-red-500" : "bg-emerald-500"
                )}>
                  {orphanFiles.length > 0 ? <AlertTriangle size={20} /> : <CheckCircle size={20} />}
                </div>
                <div>
                  <p className={cn("text-xs", orphanFiles.length > 0 ? "text-red-600" : "text-emerald-600")}>
                    孤儿文件
                  </p>
                  <p className={cn("text-xl font-bold", orphanFiles.length > 0 ? "text-red-900" : "text-emerald-900")}>
                    {orphanFiles.length.toLocaleString()}
                  </p>
                  <p className={cn("text-xs", orphanFiles.length > 0 ? "text-red-600" : "text-emerald-600")}>
                    {formatFileSize(orphanFiles.reduce((sum, f) => sum + f.size, 0))}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={fetchFolderStats}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              刷新统计
            </button>
            
            <button
              onClick={scanOrphanFiles}
              disabled={isScanning || isDeleting}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
            >
              {isScanning ? <Loader2 size={16} className="animate-spin" /> : <AlertTriangle size={16} />}
              {isScanning ? '扫描中...' : '扫描孤儿文件'}
            </button>

            {orphanFiles.length > 0 && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isDeleting}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {isDeleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                删除孤儿文件 ({orphanFiles.length})
              </button>
            )}
          </div>

          {/* 删除进度 */}
          {isDeleting && deleteProgress && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-red-700">正在删除孤儿文件...</span>
                <span className="text-sm font-mono text-red-700">{deleteProgress.current} / {deleteProgress.total}</span>
              </div>
              <div className="h-2 bg-red-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-red-500 transition-all"
                  style={{ width: `${(deleteProgress.current / deleteProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* 扫描结果 */}
          {scanProgress && (
            <div className={cn(
              "border rounded-xl p-4",
              scanProgress.includes('✅') ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"
            )}>
              <p className={cn(
                "text-sm whitespace-pre-line",
                scanProgress.includes('✅') ? "text-emerald-800" : "text-amber-800"
              )}>{scanProgress}</p>
            </div>
          )}

          {/* 错误提示 */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* 孤儿文件列表 */}
          {orphanFiles.length > 0 && (
            <div className="bg-white border border-red-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-red-50 border-b border-red-200">
                <h3 className="text-sm font-semibold text-red-700">
                  🔴 孤儿文件列表 ({orphanFiles.length} 个, {formatFileSize(orphanFiles.reduce((s, f) => s + f.size, 0))})
                </h3>
              </div>
              <div className="max-h-48 overflow-y-auto divide-y divide-red-50">
                {orphanFiles.slice(0, 100).map((file, index) => (
                  <div key={file.key} className="px-4 py-2 text-xs flex justify-between hover:bg-red-50">
                    <span className="font-mono text-slate-600 truncate flex-1">{file.key}</span>
                    <span className="text-slate-500 ml-2">{formatFileSize(file.size)}</span>
                  </div>
                ))}
                {orphanFiles.length > 100 && (
                  <div className="px-4 py-2 text-xs text-center text-slate-500">
                    ... 还有 {orphanFiles.length - 100} 个文件
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 文件夹统计列表 */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-700">
                📁 文件夹统计 ({folderStats.length} 个用户)
              </h3>
            </div>
            
            {loading ? (
              <div className="p-8 text-center text-slate-500">
                <Loader2 size={24} className="mx-auto animate-spin mb-2" />
                加载中...
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {folderStats.map((folder, index) => (
                  <div key={folder.user_folder} className="p-4 hover:bg-slate-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          "p-2 rounded-lg",
                          index === 0 ? "bg-orange-100 text-orange-600" : "bg-slate-100 text-slate-600"
                        )}>
                          <FolderOpen size={18} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-800 font-mono text-sm">
                              {folder.user_folder.substring(0, 8)}...
                            </p>
                            {folder.user_email && (
                              <span className="flex items-center gap-1 text-xs text-slate-500">
                                <User size={12} />
                                {folder.user_email}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            {folder.file_count.toLocaleString()} 个文件
                          </p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className={cn(
                          "text-lg font-bold",
                          folder.size_bytes > 1024 * 1024 * 1024 ? "text-orange-600" : "text-slate-700"
                        )}>
                          {formatFileSize(folder.size_bytes)}
                        </p>
                        <div className="w-24 h-1.5 bg-slate-200 rounded-full mt-1">
                          <div 
                            className={cn(
                              "h-full rounded-full",
                              index === 0 ? "bg-orange-500" : "bg-slate-400"
                            )}
                            style={{ width: `${Math.min((folder.size_bytes / (folderStats[0]?.size_bytes || 1)) * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 底部 */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
          >
            关闭
          </button>
        </div>
      </div>

      {/* 删除确认弹窗 */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={deleteOrphanFiles}
        type="danger"
        title="确认删除孤儿文件"
        message={`确定要删除 ${orphanFiles.length} 个孤儿文件吗？这些文件在 R2 存储中存在，但数据库中没有记录。删除后无法恢复！`}
        confirmText="确认删除"
        cancelText="取消"
      />
    </div>
  );
};

export default StorageCleanup;
